
import { useAccessContext } from "../component/guard/AccessGuard"
import { api, firestore } from "../core"
import { useToast } from "popopo-lib-front/hook"
import { useZodForm } from "popopo-lib-front/zod-form"
import { useMyStories, useStoryTimelines } from "popopo-lib-front/firestore-access"
import { UserId, FirestorePaths, CreateStoryRequest } from "popopo-msg"
import type { CreateStoryRequest as CreateStoryRequestType } from "popopo-msg"
import * as Firestore from "firebase/firestore"
import { Spinner } from "react-bootstrap"

export const Stories = () => {
  const context = useAccessContext()
  const toast = useToast()
  const myStories = useMyStories(firestore, UserId(context.user.id))
  const storyTimelines = useStoryTimelines(firestore, UserId(context.user.id))

  const createForm = useZodForm({
    schema: CreateStoryRequest,
    mode: "onBlur",
    defaultValues: {
      message: "",
    },
  })
  const { isSubmitting, isValid } = createForm.formState

  const onCreateStory = async (values: CreateStoryRequestType) => {
    try {
      const idToken = await context.credential.user.getIdToken()
      const result = await api.POST("/api/v2/users/me/stories", {
        headers: {
          authorization: `Bearer ${idToken}`,
        },
        body: {
          message: values.message,
        },
      })

      if (result.error) {
        toast.add({ type: "danger", title: "Story Create", message: result.error.message || "エラーが発生しました" })
      } else {
        toast.add({ type: "success", title: "Story Create", message: "ストーリーを作成しました" })
        createForm.reset()
      }
    } catch (e) {
      console.error(e)
      toast.add({ type: "danger", title: "Story Create", message: "ストーリーの作成に失敗しました" })
    }
  }

  const onDeleteStory = async (storyId: string) => {
    try {
      const idToken = await context.credential.user.getIdToken()
      const result = await api.DELETE("/api/v2/users/me/stories/{story_id}", {
        params: { path: { story_id: storyId } },
        headers: {
          authorization: `Bearer ${idToken}`,
        },
      })

      if (result.error) {
        toast.add({ type: "danger", title: "Story Delete", message: result.error.message || "エラーが発生しました" })
      } else {
        toast.add({ type: "success", title: "Story Delete", message: "ストーリーを削除しました" })
      }
    } catch (e) {
      console.error(e)
      toast.add({ type: "danger", title: "Story Delete", message: "ストーリーの削除に失敗しました" })
    }
  }

  const onMarkAllAsRead = async (followeeId: string) => {
    try {
      const timelinePath = FirestorePaths.UserPrivates.StoryTimeline.getDocPath(UserId(context.user.id), UserId(followeeId))
      const docRef = Firestore.doc(firestore, timelinePath)
      const docSnapshot = await Firestore.getDoc(docRef)

      if (!docSnapshot.exists()) {
        toast.add({ type: "danger", title: "Mark as Read", message: "タイムラインが見つかりません" })
        return
      }

      const timeline = storyTimelines !== "loading" ? storyTimelines.find(t => t.id === followeeId) : null
      if (!timeline) {
        toast.add({ type: "danger", title: "Mark as Read", message: "タイムラインが見つかりません" })
        return
      }

      const maxCreatedAt = timeline.last_created_at

      await Firestore.updateDoc(docRef, {
        has_unread: false,
        read_at: maxCreatedAt
      })

      toast.add({ type: "success", title: "Mark as Read", message: "すべて既読にしました" })
    } catch (e) {
      console.error(e)
      toast.add({ type: "danger", title: "Mark as Read", message: "既読処理に失敗しました" })
    }
  }

  const onMarkAllAsUnread = async (followeeId: string) => {
    try {
      const timelinePath = FirestorePaths.UserPrivates.StoryTimeline.getDocPath(UserId(context.user.id), UserId(followeeId))
      const docRef = Firestore.doc(firestore, timelinePath)
      const docSnapshot = await Firestore.getDoc(docRef)

      if (!docSnapshot.exists()) {
        toast.add({ type: "danger", title: "Mark as Unread", message: "タイムラインが見つかりません" })
        return
      }

      const timeline = storyTimelines !== "loading" ? storyTimelines.find(t => t.id === followeeId) : null
      if (!timeline) {
        toast.add({ type: "danger", title: "Mark as Unread", message: "タイムラインが見つかりません" })
        return
      }

      await Firestore.updateDoc(docRef, {
        has_unread: true,
        read_at: null
      })

      toast.add({ type: "success", title: "Mark as Unread", message: "すべて未読に戻しました" })
    } catch (e) {
      console.error(e)
      toast.add({ type: "danger", title: "Mark as Unread", message: "未読化処理に失敗しました" })
    }
  }

  const onMarkStoryAsRead = async (followeeId: string, readAt: number) => {
    try {
      const timelinePath = FirestorePaths.UserPrivates.StoryTimeline.getDocPath(UserId(context.user.id), UserId(followeeId))
      const docRef = Firestore.doc(firestore, timelinePath)
      const docSnapshot = await Firestore.getDoc(docRef)

      if (!docSnapshot.exists()) {
        toast.add({ type: "danger", title: "Mark as Read", message: "タイムラインが見つかりません" })
        return
      }

      const timeline = storyTimelines !== "loading" ? storyTimelines.find(t => t.id === followeeId) : null
      if (!timeline) {
        toast.add({ type: "danger", title: "Mark as Read", message: "タイムラインが見つかりません" })
        return
      }

      const hasUnread = timeline.stories.some(s => readAt < s.created_at)

      await Firestore.updateDoc(docRef, {
        has_unread: hasUnread,
        read_at: readAt
      })

      toast.add({ type: "success", title: "Mark as Read", message: "既読にしました" })
    } catch (e) {
      console.error(e)
      toast.add({ type: "danger", title: "Mark as Read", message: "既読処理に失敗しました" })
    }
  }

  const onMarkStoryAsUnread = async (followeeId: string, readAt: number) => {
    try {
      const timelinePath = FirestorePaths.UserPrivates.StoryTimeline.getDocPath(UserId(context.user.id), UserId(followeeId))
      const docRef = Firestore.doc(firestore, timelinePath)
      const docSnapshot = await Firestore.getDoc(docRef)

      if (!docSnapshot.exists()) {
        toast.add({ type: "danger", title: "Mark as Unread", message: "タイムラインが見つかりません" })
        return
      }

      const timeline = storyTimelines !== "loading" ? storyTimelines.find(t => t.id === followeeId) : null
      if (!timeline) {
        toast.add({ type: "danger", title: "Mark as Unread", message: "タイムラインが見つかりません" })
        return
      }

      const hasUnread = timeline.stories.some(s => readAt < s.created_at)

      await Firestore.updateDoc(docRef, {
        has_unread: hasUnread,
        read_at: readAt
      })

      toast.add({ type: "success", title: "Mark as Unread", message: "未読に戻しました" })
    } catch (e) {
      console.error(e)
      toast.add({ type: "danger", title: "Mark as Unread", message: "未読化処理に失敗しました" })
    }
  }

  return (
    <div className="row gy-5 justify-content-center">
      <div className="d-flex col-12 col-xl-6 flex-column gap-3 align-items-center">
        <h3>ストーリー作成</h3>
        <form className="d-flex w-100 flex-column gap-3" onSubmit={createForm.handleSubmit(onCreateStory)}>
          <div className="form-floating">
            <textarea
              className="form-control"
              style={{ height: "100px" }}
              placeholder="メッセージ"
              {...createForm.register("message")}
              disabled={isSubmitting}
            />
            <label>メッセージ</label>
          </div>

          <button
            type="submit"
            className="btn btn-primary form-control"
            disabled={isSubmitting || !createForm.watch("message") || !isValid}
          >
            <i className="fas fa-plus me-2" />
            ストーリーを作成
          </button>
        </form>

        <h3 className="mt-4">自分のストーリー</h3>
        {myStories === "loading" ? (
          <Spinner />
        ) : myStories.length === 0 ? (
          <p>ストーリーはありません</p>
        ) : (
          <div className="d-flex w-100 flex-column gap-3">
            {myStories.map((story) => (
              <div key={story.id} className="card">
                <div className="card-body">
                  <p className="card-text">{story.message}</p>
                  <p className="card-text">
                    <small className="text-muted">
                      作成日時: {new Date(story.created_at).toLocaleString()}
                    </small>
                  </p>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => onDeleteStory(story.id)}
                  >
                    <i className="fas fa-trash me-2" />
                    削除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="d-flex col-12 col-xl-6 flex-column gap-3 align-items-center">
        <h3>フォロー中のストーリー</h3>
        {storyTimelines === "loading" ? (
          <Spinner />
        ) : storyTimelines.length === 0 ? (
          <p>フォロー中のストーリーはありません</p>
        ) : (
          <div className="d-flex w-100 flex-column gap-3">
            {storyTimelines.map((timeline) => (
              <div key={timeline.id} className="card">
                <div className="card-body">
                  <h5 className="card-title">
                    ユーザーID: {timeline.id}
                    {timeline.has_unread && (
                      <span className="badge ms-2 bg-primary">未読あり</span>
                    )}
                  </h5>
                  <p className="card-text">
                    <small className="text-muted">
                      ストーリー数: {timeline.stories.length} / 最終投稿: {new Date(timeline.last_created_at).toLocaleString()}
                    </small>
                  </p>
                  {timeline.read_at !== null && (
                    <p className="card-text">
                      <small className="text-muted">
                        最終閲覧: {new Date(timeline.read_at).toLocaleString()}
                      </small>
                    </p>
                  )}
                  <div className="mt-3">
                    <div className="d-flex justify-content-between mb-2 align-items-center">
                      <h6 className="mb-0">ストーリー一覧:</h6>
                      <div className="d-flex gap-2">
                        {timeline.has_unread && (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => onMarkAllAsRead(timeline.id)}
                          >
                            <i className="fas fa-check me-2" />
                            すべて既読にする
                          </button>
                        )}
                        {!timeline.has_unread && (
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => onMarkAllAsUnread(timeline.id)}
                          >
                            <i className="fas fa-undo me-2" />
                            すべて未読に戻す
                          </button>
                        )}
                      </div>
                    </div>
                    {timeline.stories.map((story) => {
                      const isRead = timeline.read_at !== null && story.created_at <= timeline.read_at
                      return (
                        <div key={story.id} className={`mb-2 rounded border p-2 ${isRead ? "bg-light" : "border-primary bg-white"}`}>
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <p className="mb-1">{story.message}</p>
                              <small className="text-muted">
                                {new Date(story.created_at).toLocaleString()}
                                {isRead ? " (既読)" : " (未読)"}
                              </small>
                            </div>
                            <div className="d-flex ms-2 gap-1">
                              {isRead ? (
                                <button
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() => onMarkStoryAsUnread(timeline.id, story.created_at - 1)}
                                  title="未読に戻す"
                                >
                                  <i className="fas fa-undo" />
                                </button>
                              ) : (
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => onMarkStoryAsRead(timeline.id, story.created_at)}
                                  title="既読にする"
                                >
                                  <i className="fas fa-check" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
